import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Clear legacy localStorage users array on app load
  useEffect(() => {
    localStorage.removeItem('users');
  }, []);

  useEffect(() => {
    // Check for stored user data on app load
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const storedAdmin = localStorage.getItem('isAdmin');
    
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    
    if (storedToken) {
      setToken(storedToken);
    }
    
    if (storedAdmin === 'true') {
      setIsAdmin(true);
    }
    
    setLoading(false);
  }, []);

  const generateOTP = () => {
    // return Math.floor(100000 + Math.random() * 900000).toString();
    return '123456';
  };

  const register = async (registrationData) => {
    try {
      const response = await fetch('http://localhost:5000/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationData)
      });
      const data = await response.json();
      if (data.success) {
        setUser(data.data);
        setToken(data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data));
        localStorage.setItem('token', data.data.token);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Registration failed' };
    }
  };

  const verifyOTP = async (phoneNumber, enteredOTP) => {
    try {
      const tempData = localStorage.getItem('tempRegistration');
      if (!tempData) {
        toast.error('No registration data found');
        return { success: false };
      }

      const { otp, name, society, address } = JSON.parse(tempData);
      
      if (enteredOTP === otp) {
        // Get all users
        let users = JSON.parse(localStorage.getItem('users') || '[]');
        // Check if user already exists
        if (users.some(u => u.phoneNumber === phoneNumber)) {
          toast.error('User already registered. Please login.');
          return { success: false };
        }
        const newUser = {
          id: Date.now().toString(),
          phoneNumber,
          name,
          society,
          address,
          createdAt: new Date().toISOString()
        };
        users.push(newUser);
        localStorage.setItem('users', JSON.stringify(users));
        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
        localStorage.removeItem('tempRegistration');
        
        toast.success('Registration successful!');
        return { success: true };
      } else {
        toast.error('Invalid OTP');
        return { success: false };
      }
    } catch (error) {
      toast.error('OTP verification failed');
      return { success: false };
    }
  };

  const login = async (loginData) => {
    try {
      console.log('Login attempt with:', loginData);
      const response = await fetch('http://localhost:5000/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const data = await response.json();
      console.log('Login response:', data);
      if (data.success) {
        setUser(data.data);
        setToken(data.data.token);
        setIsAdmin(data.data.isAdmin);
        localStorage.setItem('user', JSON.stringify(data.data));
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('isAdmin', data.data.isAdmin ? 'true' : 'false');
        console.log('Login successful, token stored:', data.data.token);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed' };
    }
  };

  const adminLogin = async (username, password) => {
    try {
      const response = await fetch('http://localhost:5000/api/auth/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await response.json();
      if (data.success) {
        setUser(data.data);
        setToken(data.data.token);
        setIsAdmin(true);
        localStorage.setItem('user', JSON.stringify(data.data));
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('isAdmin', 'true');
        toast.success('Admin login successful!');
        return { success: true };
      } else {
        toast.error(data.message || 'Invalid admin credentials');
        return { success: false };
      }
    } catch (error) {
      toast.error('Admin login failed');
      return { success: false };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAdmin(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    token,
    isAdmin,
    loading,
    setLoading,
    register,
    verifyOTP,
    login,
    adminLogin,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 